import React from "react";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import SendIcon from "@mui/icons-material/Send";
import CircularProgress from "@mui/material/CircularProgress";
import { toast } from "react-toastify";
import { getRightworkAPI, postRightworkAPI } from "../../requests/utilities/requests";
import {
  API_get_plan_task_comments,
  API_post_plan_task_comment,
  API_get_plan_task_comments_kiosk,
  API_post_plan_task_comment_kiosk,
} from "../../../../constants";

// The comment thread on one task occurrence, the same anatomy as the ticket
// drawer's Conversation tab: avatar, name, time, text; Enter posts,
// Shift+Enter is a newline. The hook owns the data so the drawer can pin the
// composer in its footer while the employee modal keeps it inline.

// wholeDay: the thread of every slot of an hourly task on that day (the
// occurrence named is the one a new comment is posted on); `slots` then lists
// the day's slots with their counts.
// kioskParams: on the shared-device kiosk, the identity of the person at the
// screen ({store_team_member_id} or {clock_pin}); the thread then goes through
// the *_kiosk twins, so a comment is signed by that person, not the kiosk login.
export function useTaskComments({ storeId, occurrenceUuid, occurrenceDate, onPosted, wholeDay = false, kioskParams = null }) {
  const [comments, setComments] = React.useState([]);
  const [slots, setSlots] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [posting, setPosting] = React.useState(false);
  const seqRef = React.useRef(0);
  // The occurrence the hook currently shows. A post resolving after the user
  // switched slots or tasks must not land in the new thread.
  const activeRef = React.useRef(occurrenceUuid);
  activeRef.current = occurrenceUuid;

  // showLoading: the first load of a thread shows the spinner; a reconcile
  // after a post keeps the list on screen (as the ticket drawer does) and
  // only swaps in the server's copy when it lands.
  const fetchComments = React.useCallback(async ({ showLoading = true } = {}) => {
    if (!storeId || !occurrenceUuid) return;
    const seq = ++seqRef.current;
    if (showLoading) setLoading(true);
    try {
      const res = await getRightworkAPI(kioskParams ? API_get_plan_task_comments_kiosk : API_get_plan_task_comments, {
        store_id: storeId,
        plan_task_assignment_uuid: occurrenceUuid,
        specific_date: occurrenceDate,
        ...(wholeDay ? { whole_day: "true" } : {}),
        ...(kioskParams || {}),
      });
      if (seqRef.current !== seq) return;
      const payload = res.data?.redux?.payload;
      setComments(payload?.plan_task_comments || []);
      setSlots(payload?.slots || []);
    } catch (error) {
      if (seqRef.current !== seq) return;
      console.error("Error fetching task comments:", error);
      // A failed reconcile leaves what is on screen; only a first load clears.
      if (showLoading) {
        setComments([]);
        setSlots([]);
      }
    } finally {
      // The latest fetch always settles the spinner, so a reconcile that
      // overtook a first load cannot leave it spinning.
      if (seqRef.current === seq) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, occurrenceUuid, occurrenceDate, wholeDay, kioskParams?.store_team_member_id, kioskParams?.clock_pin]);

  React.useEffect(() => {
    setComments([]);
    fetchComments();
  }, [fetchComments]);

  const post = React.useCallback(
    async (text) => {
      const trimmed = (text || "").trim();
      if (!trimmed || posting) return false;
      const postedFor = occurrenceUuid;
      setPosting(true);
      try {
        const res = await postRightworkAPI(kioskParams ? API_post_plan_task_comment_kiosk : API_post_plan_task_comment, {
          store_id: storeId,
          plan_task_assignment_uuid: postedFor,
          specific_date: occurrenceDate,
          text: trimmed,
          ...(kioskParams || {}),
        });
        const payload = res.data?.redux?.payload;
        const created = payload?.plan_task_comment;
        if (activeRef.current === postedFor) {
          if (created) {
            // Show it at once. The day thread labels each comment by slot:
            // the one it was posted on, which the slots list already names.
            const slot = wholeDay ? slots.find((x) => x.plan_task_assignment_uuid === postedFor) : null;
            const shown = wholeDay
              ? {
                  ...created,
                  plan_task_assignment_uuid: postedFor,
                  slot_index: slot?.slot_index ?? null,
                  slot_label: slot?.slot_label ?? null,
                }
              : created;
            setComments((prev) => (prev.some((c) => c.uuid === shown.uuid) ? prev : [...prev, shown]));
            if (wholeDay && slot) {
              setSlots((prev) =>
                prev.map((x) =>
                  x.plan_task_assignment_uuid === postedFor ? { ...x, comment_count: (x.comment_count || 0) + 1 } : x
                )
              );
            }
          }
          // Reconcile with the server's copy without taking the list away.
          fetchComments({ showLoading: false });
        }
        // The row behind the thread still changed, whichever thread is showing.
        onPosted?.(payload?.comment_count ?? comments.length + 1);
        return true;
      } catch (error) {
        toast.error(error?.response?.data?.system?.message || "Failed to post comment");
        return false;
      } finally {
        setPosting(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storeId, occurrenceUuid, occurrenceDate, posting, onPosted, comments.length, wholeDay, slots, fetchComments, kioskParams?.store_team_member_id, kioskParams?.clock_pin]
  );

  return { comments, slots, loading, posting, post, refresh: () => fetchComments() };
}

export function CommentList({ comments, loading, emptyHint = "Start the conversation below", dense = false }) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <CircularProgress size={24} sx={{ color: "#6d28d9" }} />
      </div>
    );
  }
  if (!comments.length) {
    return (
      <div className="flex flex-col items-center justify-center py-10 text-slate-400">
        <p className="text-sm">No comments yet</p>
        <p className="text-xs mt-1">{emptyHint}</p>
      </div>
    );
  }
  return (
    <div className={dense ? "space-y-3" : "space-y-4"}>
      {comments.map((comment) => (
        <div key={comment.uuid} className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-violet-100 text-violet-700 flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {comment.author?.initials || "?"}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-slate-900">{comment.author?.full_name}</p>
              <p className="text-xs text-slate-400">{comment.created_at_formatted}</p>
              {comment.slot_label && (
                <span className="text-[10px] font-semibold rounded-full px-1.5 py-px bg-slate-100 text-slate-500">
                  {comment.slot_label}
                </span>
              )}
            </div>
            <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{comment.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

export function CommentComposer({ onPost, posting, placeholder = "Add a comment...", hint = null }) {
  const [text, setText] = React.useState("");
  const submit = async () => {
    if (await onPost(text)) setText("");
  };
  return (
    <div>
    {hint && <p className="text-[11px] text-slate-400 mb-1.5">{hint}</p>}
    <div className="flex items-end gap-2">
      <TextField
        fullWidth
        placeholder={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
        }}
        multiline
        maxRows={4}
        size="small"
        sx={{
          "& .MuiOutlinedInput-root": {
            backgroundColor: "white",
            borderRadius: "8px",
            "&.Mui-focused fieldset": { borderColor: "#6d28d9" },
          },
        }}
      />
      <IconButton
        onClick={submit}
        disabled={!text.trim() || posting}
        sx={{
          backgroundColor: text.trim() ? "#6d28d9" : "#e2e8f0",
          color: "white",
          width: 40,
          height: 40,
          "&:hover": { backgroundColor: text.trim() ? "#5b21b6" : "#e2e8f0" },
          "&.Mui-disabled": { backgroundColor: "#e2e8f0", color: "#94a3b8" },
        }}
      >
        {posting ? <CircularProgress size={18} sx={{ color: "white" }} /> : <SendIcon sx={{ fontSize: 18 }} />}
      </IconButton>
    </div>
    </div>
  );
}
